import { getUserError } from '../../../components/miscUtils';
import { getColumnInfo, getDendrogram, getGroupInfo } from '../../../data/analyticsDataSlice';
import { TpDataContext, TpDataTable } from '../../../data/interfaces';
import {
  getDataTable,
  getDataTableByCanonicalType,
  getDendrogramBinding,
  isDendrogramBinding,
  TpChartConfigParams,
  TpChartDataBinding,
} from '../charts/chartDataBindings';
import {
  DOCVIEWER_ELEMENT_TYPES,
  TpMarkdownDocViewerElement,
  TpPropertiesDocViewerElement,
  TpPropertyDocViewerElement,
  TpDocViewerPropertyDescription,
  TpChartManifestAspectDef,
  TpChartManifestConfigParamDef,
  TpDocViewerElement,
  TpDocViewerElementGenerator,
} from '../charts/interfaces';

function unboundAspectElement(): TpMarkdownDocViewerElement {
  return {
    type: DOCVIEWER_ELEMENT_TYPES.STRING,
    id: 'unbound-aspect',
    content: '*Not bound to any data.*',
  };
}

function getDataTableForAspect(
  aspect: TpChartManifestAspectDef,
  dataContext: TpDataContext,
  currentDataBinding: TpChartDataBinding,
  onSecondaryTable: boolean,
) {
  if (onSecondaryTable) {
    return getDataTable(dataContext, currentDataBinding.secondDataTableId!);
  }
  if (aspect.onColumnItems) {
    // datamatrix visualisation, and we want to look in table that is linked to the column
    if (!currentDataBinding.columnsCanonicalTypeId)
      throw getUserError(`Aspect is defined for column linked table but no column linked table was provided`);
    return getDataTableByCanonicalType(dataContext, currentDataBinding.columnsCanonicalTypeId!);
  }
  return getDataTable(dataContext, currentDataBinding.dataTableId);
}

function propertyDescription(dataContext: TpDataContext, dataTable: TpDataTable, columnId: string) {
  if (isDendrogramBinding(columnId)) {
    const dendrogram = getDendrogram(dataContext, getDendrogramBinding(columnId));
    return {
      name: dendrogram.name,
      description: dendrogram.description || null,
    };
  }
  const columnInfo = getColumnInfo(dataTable, columnId);
  if (!columnInfo) {
    return null;
  }
  return {
    name: columnInfo.name,
    description: columnInfo.description || null,
  };
}

function propertyElement(columnId: string, propertyDescription: TpDocViewerPropertyDescription | null) {
  if (!propertyDescription) {
    return unboundAspectElement();
  }

  const propertyElement: TpPropertyDocViewerElement = {
    type: DOCVIEWER_ELEMENT_TYPES.PROPERTY,
    id: columnId,
    content: propertyDescription,
  };
  return propertyElement;
}

function documentationElements(
  documentationProp: undefined | TpDocViewerElement[] | TpDocViewerElementGenerator,
  dataContext: TpDataContext,
  currentDataBinding: TpChartDataBinding,
  currentConfigParams: TpChartConfigParams,
) {
  if (!documentationProp) {
    return [];
  } else if (documentationProp instanceof Function) {
    return documentationProp(currentConfigParams, dataContext, currentDataBinding);
  }
  return documentationProp;
}

export function getAspectElements(
  aspect: TpChartManifestAspectDef,
  dataContext: TpDataContext,
  currentDataBinding: TpChartDataBinding,
  currentConfigParams: TpChartConfigParams,
) {
  const elements = [];
  const columnId = currentDataBinding.aspectBindings[aspect.id]?.columnId;
  if (!columnId) {
    elements.push(unboundAspectElement());
  } else {
    const dataTable = getDataTableForAspect(aspect, dataContext, currentDataBinding, false);
    elements.push(propertyElement(columnId, propertyDescription(dataContext, dataTable, columnId)));
  }
  elements.push(...documentationElements(aspect.documentation, dataContext, currentDataBinding, currentConfigParams));
  return elements;
}

export function getSecondaryAspectElements(
  aspect: TpChartManifestAspectDef,
  dataContext: TpDataContext,
  currentDataBinding: TpChartDataBinding,
  currentConfigParams: TpChartConfigParams,
) {
  const elements = [];
  const columnId = currentDataBinding.secondAspectBindings![aspect.id]?.columnId;
  if (!columnId) {
    elements.push(unboundAspectElement());
  } else {
    const dataTable = getDataTableForAspect(aspect, dataContext, currentDataBinding, true);
    elements.push(propertyElement(columnId, propertyDescription(dataContext, dataTable, columnId)));
  }
  elements.push(...documentationElements(aspect.documentation, dataContext, currentDataBinding, currentConfigParams));
  return elements;
}

export function getCompoundAspectElements(
  aspect: TpChartManifestAspectDef,
  dataContext: TpDataContext,
  currentDataBinding: TpChartDataBinding,
  currentConfigParams: TpChartConfigParams,
) {
  const elements = [];
  const columnIds = currentDataBinding.compoundAspectBindings![aspect.id];
  if (!columnIds || columnIds.length === 0) {
    elements.push(unboundAspectElement());
  } else {
    const dataTable = getDataTableForAspect(aspect, dataContext, currentDataBinding, false);
    const columns = columnIds
      .map((columnId) => propertyDescription(dataContext, dataTable, columnId))
      .filter((column) => column !== null);

    const propertiesElement: TpPropertiesDocViewerElement = {
      type: DOCVIEWER_ELEMENT_TYPES.PROPERTIES,
      id: aspect.id,
      content: columns,
    };

    elements.push(propertiesElement);
  }
  elements.push(...documentationElements(aspect.documentation, dataContext, currentDataBinding, currentConfigParams));
  return elements;
}

export function getConfigParamElements(
  configParam: TpChartManifestConfigParamDef,
  dataContext: TpDataContext,
  currentDataBinding: TpChartDataBinding,
  currentConfigParams: TpChartConfigParams,
) {
  return documentationElements(configParam.documentation, dataContext, currentDataBinding, currentConfigParams);
}

export function getDataMatrixElements(dataContext: TpDataContext, currentDataBinding: TpChartDataBinding) {
  const groupId = currentDataBinding.columnsGroupId;
  const columnCanonicalTypeId = currentDataBinding.columnsCanonicalTypeId;
  if (!groupId || !columnCanonicalTypeId) {
    return [unboundAspectElement()];
  }

  const dataTable = getDataTable(dataContext, currentDataBinding.dataTableId);
  const propertyGroup = getGroupInfo(dataTable, groupId);

  const descriptionElement: TpPropertyDocViewerElement = {
    type: DOCVIEWER_ELEMENT_TYPES.PROPERTY,
    id: 'description',
    content: { name: propertyGroup.name, description: propertyGroup.description || '*No description available*' },
  };

  return [descriptionElement];
}
